import React, { useEffect, useState } from 'react';
import AdminLayout from '@/components/layout/AdminLayout';
import { supabase } from '@/integrations/supabase/client';
import { motion } from 'framer-motion';
import { 
  Plus, 
  Trash2, 
  Save, 
  Loader2, 
  AlertCircle, 
  CheckCircle2, 
  Bed,
  Square,
  Users,
  Image as ImageIcon,
  Upload,
  ArrowUp,
  ArrowDown
} from 'lucide-react';

const AdminRooms = () => {
  const [rooms, setRooms] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error', text: string } | null>(null);

  useEffect(() => {
    fetchRooms();
  }, []);

  const fetchRooms = async () => {
    try {
      const { data, error } = await supabase
        .from('rooms')
        .select('*')
        .order('display_order', { ascending: true });
      
      if (error) throw error;
      setRooms(data || []);
    } catch (error) {
      console.error('Error fetching rooms:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleAddRoom = () => {
    const newRoom = {
      name: 'New Room',
      description: 'Room description...',
      image_url: '',
      capacity: '2 Adults',
      size: '250 sq ft',
      amenities: [],
      display_order: rooms.length,
      is_active: true,
      isNew: true,
      tempId: Math.random().toString(36).substr(2, 9)
    };
    setRooms([...rooms, newRoom]);
  };

  const handleChange = (id: string, field: string, value: any, isTemp = false) => {
    setRooms(prev => prev.map(r => {
      const match = isTemp ? r.tempId === id : r.id === id;
      return match ? { ...r, [field]: value } : r;
    }));
  };

  const moveRoom = (index: number, direction: 'up' | 'down') => {
    const newRooms = [...rooms];
    const newIndex = direction === 'up' ? index - 1 : index + 1;
    if (newIndex >= 0 && newIndex < newRooms.length) {
      [newRooms[index], newRooms[newIndex]] = [newRooms[newIndex], newRooms[index]];
      setRooms(newRooms.map((r, i) => ({ ...r, display_order: i })));
    }
  };

  const handleSave = async () => {
    setSaving(true);
    setMessage(null);
    try {
      for (const room of rooms) {
        const { isNew, tempId, ...saveData } = room;
        if (isNew) {
          const { error } = await supabase.from('rooms').insert([saveData]);
          if (error) throw error;
        } else {
          const { error } = await supabase.from('rooms').update(saveData).eq('id', room.id);
          if (error) throw error;
        }
      }
      setMessage({ type: 'success', text: 'Rooms updated successfully!' });
      fetchRooms();
    } catch (error: any) {
      setMessage({ type: 'error', text: error.message });
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id: string, isTemp = false) => {
    if (!confirm('Are you sure you want to delete this room?')) return;
    if (isTemp) {
      setRooms(prev => prev.filter(r => r.tempId !== id));
      return;
    }
    try {
      const { error } = await supabase.from('rooms').delete().eq('id', id);
      if (error) throw error;
      setRooms(prev => prev.filter(r => r.id !== id));
    } catch (error: any) {
      setMessage({ type: 'error', text: error.message });
    }
  };

  if (loading) return <AdminLayout><div className="flex items-center justify-center h-full"><Loader2 className="animate-spin text-emerald" /></div></AdminLayout>;

  return (
    <AdminLayout>
      <div className="space-y-8">
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-4xl font-serif text-emerald">Room Management</h1>
            <p className="text-[#59635C] font-clean mt-2">Manage room details and features.</p>
          </div>
          <div className="flex gap-4">
            <button
              onClick={handleAddRoom}
              className="bg-white text-emerald border border-emerald px-6 py-3 rounded-full font-clean font-bold uppercase tracking-widest hover:bg-emerald/5 transition-all flex items-center gap-2"
            >
              <Plus className="w-4 h-4" />
              Add Room
            </button>
            <button
              onClick={handleSave}
              disabled={saving}
              className="bg-emerald text-ivory px-8 py-3 rounded-full font-clean font-bold uppercase tracking-widest hover:bg-emerald/90 transition-all flex items-center gap-2 shadow-lg disabled:opacity-50"
            >
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
              Save Rooms
            </button>
          </div>
        </div>

        {message && (
          <div className={`p-4 rounded-xl flex items-center gap-3 ${message.type === 'success' ? 'bg-green-50 text-green-700 border border-green-100' : 'bg-red-50 text-red-700 border border-red-100'}`}>
            {message.type === 'success' ? <CheckCircle2 className="w-5 h-5" /> : <AlertCircle className="w-5 h-5" />}
            <span className="font-clean font-medium">{message.text}</span>
          </div>
        )}

        <div className="grid grid-cols-1 gap-6">
          {rooms.map((room) => (
            <div key={room.id || room.tempId} className="bg-white p-6 rounded-2xl border border-accent/10 shadow-sm grid grid-cols-1 md:grid-cols-4 gap-6 relative group">
              <div className="absolute top-4 right-14 flex gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
                <button 
                  onClick={() => moveRoom(rooms.indexOf(room), 'up')}
                  disabled={rooms.indexOf(room) === 0}
                  className="p-2 text-emerald hover:bg-emerald/5 rounded-lg disabled:opacity-30"
                >
                  <ArrowUp className="w-5 h-5" />
                </button>
                <button 
                  onClick={() => moveRoom(rooms.indexOf(room), 'down')}
                  disabled={rooms.indexOf(room) === rooms.length - 1}
                  className="p-2 text-emerald hover:bg-emerald/5 rounded-lg disabled:opacity-30"
                >
                  <ArrowDown className="w-5 h-5" />
                </button>
              </div>
              <button 
                onClick={() => handleDelete(room.id || room.tempId, !!room.isNew)}
                className="absolute top-4 right-4 p-2 text-red-500 opacity-0 group-hover:opacity-100 transition-opacity hover:bg-red-50 rounded-lg"
              >
                <Trash2 className="w-5 h-5" />
              </button>

              <div className="space-y-4">
                <div className="aspect-[4/3] bg-[#F5F1E8] rounded-xl overflow-hidden flex items-center justify-center relative">
                  {room.image_url ? (
                    <img src={room.image_url} alt={room.name} className="w-full h-full object-cover" />
                  ) : (
                    <ImageIcon className="w-8 h-8 text-accent/30" />
                  )}
                </div>
                <div>
                  <label className="text-[10px] font-bold text-emerald uppercase tracking-widest font-clean block mb-1">Image URL</label>
                  <input
                    type="text"
                    value={room.image_url || ''}
                    onChange={(e) => handleChange(room.id || room.tempId, 'image_url', e.target.value, !!room.isNew)}
                    className="w-full px-3 py-2 rounded-lg border border-accent/20 focus:border-emerald outline-none font-clean bg-[#F5F1E8]/30 text-xs"
                    placeholder="/assets/..."
                  />
                </div>
              </div>

              <div className="md:col-span-2 space-y-4">
                <div>
                  <label className="text-[10px] font-bold text-emerald uppercase tracking-widest font-clean block mb-1">Room Name</label>
                  <input
                    type="text"
                    value={room.name}
                    onChange={(e) => handleChange(room.id || room.tempId, 'name', e.target.value, !!room.isNew)}
                    className="w-full px-3 py-2 rounded-lg border border-accent/20 focus:border-emerald outline-none font-clean bg-[#F5F1E8]/30 text-lg font-serif"
                  />
                </div>
                <div>
                  <label className="text-[10px] font-bold text-emerald uppercase tracking-widest font-clean block mb-1">Description</label>
                  <textarea
                    value={room.description || ''}
                    onChange={(e) => handleChange(room.id || room.tempId, 'description', e.target.value, !!room.isNew)}
                    className="w-full px-3 py-2 rounded-lg border border-accent/20 focus:border-emerald outline-none font-clean bg-[#F5F1E8]/30 text-sm min-h-[100px]"
                  />
                </div>
              </div>

              <div className="space-y-4">
                <div>
                  <label className="text-[10px] font-bold text-emerald uppercase tracking-widest font-clean block mb-1">Capacity</label>
                  <div className="flex items-center gap-2 px-3 py-2 rounded-lg border border-accent/20 bg-[#F5F1E8]/30">
                    <Users className="w-4 h-4 text-emerald" />
                    <input
                      type="text"
                      value={room.capacity || ''}
                      onChange={(e) => handleChange(room.id || room.tempId, 'capacity', e.target.value, !!room.isNew)}
                      className="w-full border-none focus:ring-0 bg-transparent text-sm p-0"
                    />
                  </div>
                </div>
                <div>
                  <label className="text-[10px] font-bold text-emerald uppercase tracking-widest font-clean block mb-1">Size</label>
                  <div className="flex items-center gap-2 px-3 py-2 rounded-lg border border-accent/20 bg-[#F5F1E8]/30">
                    <Square className="w-4 h-4 text-emerald" />
                    <input
                      type="text"
                      value={room.size || ''}
                      onChange={(e) => handleChange(room.id || room.tempId, 'size', e.target.value, !!room.isNew)}
                      className="w-full border-none focus:ring-0 bg-transparent text-sm p-0"
                    />
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </AdminLayout>
  );
};

export default AdminRooms;
